import React, { useMemo } from 'react';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import { useIntroOutroContext } from '../../context/IntroOutroContext';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { useTrimContext } from '../../context/VideoTrimContex';
import { useZoomContext } from '../../context/ZoomContext';
import ResourceHelper from '../../utils/ResourceHelper';
import Player from '../Player';
import styles from './MultipleVideosPlayer.module.scss';

const PlayerComponent = ({
  playerKey,
  sceneId,
  scenes,
  currentSceneIndex,
  currentPlayerKey,
  handleEnded,
  setPlayedProgress,
  customTiming,
  externalVolume,
  handleVolumeUpdated,
  setIsControlsVisible,
  handleTogglePlaying,
  isVideoPlaying,
  currentLanguage,
  availableLanguages,
  setCurrentLanguage,
  textPanels,
  isFirstPlayed,
  isDraft,
  hideControlsOnStart,
  thumbnailResource,
  onSeekCallback,
  externalPlaybackRate,
  handleExternalPlaybackRateChange,
  userPermissions,
  draftComponent,
  rerecordComponent,
  handleStartBackgroundMusic,
  handleStopBackgroundMusic,
  miniRightSidebar,
  isEditingRightSidebar,
  videoConfig,
}) => {
  const { trimmingOn } = useTrimContext();
  const { isTextPanelEditor } = useTextPanelContext();
  const { previewPlayedMusic } = useBackgroundMusicContext();
  const { isZoomOpened } = useZoomContext();

  const { blockPlayingIntroOutro } = useIntroOutroContext();

  const scene = useMemo(() => scenes.find(item => item.id === sceneId), [scenes, sceneId]);
  const active = useMemo(() => sceneId === scenes[currentSceneIndex]?.id, [sceneId, scenes, currentSceneIndex]);
  const sceneIndex = useMemo(() => scenes.indexOf(scene), [scene, scenes]);

  const isSingleAvailableScene = useMemo(
    () =>
      scenes.filter(item => (blockPlayingIntroOutro ? item.id !== 'Intro' && item.id !== 'Outro' : true)).filter(item => item.video)
        ?.length === 1,
    [scenes, blockPlayingIntroOutro]
  );

  const hasHlsResource = useMemo(() => {
    if (scenes[currentSceneIndex]?.id === 'Intro' || scenes[currentSceneIndex]?.id === 'Outro') {
      return scenes[currentSceneIndex]?.video !== null;
    } else {
      return ResourceHelper.getResourceByLabel(scenes[currentSceneIndex] ?? {}, 'hls') !== undefined;
    }
  }, [scenes, currentSceneIndex]);

  const isDraftComponentAllowed = useMemo(() => {
    if ((scene?.recording || (isDraft && !scene?.trimmedDuration)) && !scene?.processing) {
      const { allowedScenes, updateStory } = userPermissions;
      if (updateStory) {
        return true;
      }
      if (allowedScenes.length > 0) {
        return allowedScenes?.find(item => item === scene?.id) !== undefined;
      }
    }
    return false;
  }, [userPermissions, scene]);

  const isRerecordComponentAllowed = useMemo(() => {
    if (!isEditingRightSidebar) {
      if (scene?.video && scene?.id !== 'Intro' && scene?.id !== 'Outro') {
        const { allowedScenes, updateStory } = userPermissions;
        let isAllowed = updateStory;
        if (!isAllowed && allowedScenes.length > 0) {
          isAllowed = allowedScenes?.find(item => item === scene?.id) !== undefined;
        }
        return isAllowed;
      }
    }
    return false;
  }, [userPermissions, trimmingOn, isZoomOpened, scene, active, isEditingRightSidebar]);

  return (
    <div
      id={playerKey}
      className={styles.playerContainer}
      style={currentPlayerKey ? { display: currentPlayerKey === playerKey ? 'block' : 'none' } : {}}
    >
      <Player
        uuid={scene?.id}
        scenes={{
          trimStart: scene?.trimStart,
          trimEnd: scene?.trimEnd,
          playbackRate: scene?.playbackRate,
          zoomPoints: scene?.zoomPoints || [],
        }}
        onEnded={handleEnded}
        onError={() => console.log('ERROR')}
        overlay
        videoDuration={scene?.trimmedDuration}
        handleProgressChange={setPlayedProgress}
        hideSlider={true}
        customTiming={customTiming}
        multiplyMode={true}
        active={active}
        analytics={false}
        externalVolume={externalVolume}
        handleVolumeUpdated={handleVolumeUpdated}
        isFullScreenAllowed={false}
        bufferingDelay={300}
        controlsVisibilityChanged={setIsControlsVisible}
        processing={scene?.processing && !scene?.recording}
        handleTogglePlaying={handleTogglePlaying}
        playOnStart={isVideoPlaying && !trimmingOn && !isZoomOpened}
        enableSubtitles={false}
        showSubtitlesControlExternal={true}
        externalLanguage={currentLanguage}
        externalAvailableLanguages={availableLanguages}
        handleLanguageUpdated={setCurrentLanguage}
        blockPlaying={isTextPanelEditor || previewPlayedMusic || !scene?.video}
        textPanels={textPanels[sceneIndex]}
        isFirstPlayedExternal={isFirstPlayed}
        hidePlayControl={(isFirstPlayed && currentSceneIndex > 0) || (isDraft && !scene?.trimmedDuration) || scene?.recording}
        hideControlsOnStart={hideControlsOnStart}
        poster={scene?.recording ? null : scene?.thumbnail}
        onlyResourceThumbnail={!scene?.thumbnail && thumbnailResource?.url}
        isDraft={isDraftComponentAllowed ? draftComponent : null}
        fadeIn={currentSceneIndex === scenes.length - 1 && !trimmingOn && !isZoomOpened}
        onSeekCallback={onSeekCallback}
        externalPlaybackRate={externalPlaybackRate}
        handleExternalPlaybackRateChange={handleExternalPlaybackRateChange}
        isBackwardAndForwardActionsAllows={false}
        videoVolumeWithMusic={Number.isFinite(scene?.videoVolume) ? scene?.videoVolume : 1}
        isLazyLoadingContent={hasHlsResource}
        rerecordComponent={isRerecordComponentAllowed ? React.cloneElement(
          rerecordComponent,
          scene.enhancing ? { disableButton: true } : {} // Only override when condition is true
        ) : null}
        handleStartBackgroundMusic={handleStartBackgroundMusic}
        handleStopBackgroundMusic={handleStopBackgroundMusic}
        miniRightSidebar={miniRightSidebar}
        isSingleAvailableScene={isSingleAvailableScene}
        videoConfig={videoConfig}
      //onlyPlayControlShow={true}  // Uncomment this to hide the video controls in the player, as we probably want to show a timeline of all the clips underneath like Tella does
      />
    </div>
  );
};

export default PlayerComponent;
